/**
 * Personas — GEN-PX-022 (antes solo admin, GEN-PX-014). Botón del raíl para todos los perfiles; el kernel emite el
 * ítem `personas` a todos (R-PT-02) y la vista se adapta a lo que el kernel da a quien mira:
 *  - Administrador (el kernel le emite `admin`): la gestión de siempre, AdminPanel (invitar, cambiar rol, emails).
 *  - Resto (champion, usuario): directorio en SOLO LECTURA con nombre, cargo y rol, de GET /v1/entrada/empleados
 *    (solo activos, sin email ni ids). Pulsar una persona abre su perfil en solo lectura (GEN-PX-006).
 * Ocultar la gestión es cosmético (R-PT-04): el kernel responde 403 a un no-admin que fuerce /v1/admin/users.
 */

import { useEffect, useState } from 'react'
import AdminPanel from './AdminPanel.jsx'
import { useSession } from '../context/SessionContext.jsx'
import { useFotoAgente } from '../context/FotosAgentes.jsx'
import CabeceraLanding from './CabeceraLanding.jsx'
import { listEmpleados } from '../api/client.js'
import { Avatar, Insignia, Tarjeta } from '../despacho/ui.jsx'
import { inicialesDe, rolTexto } from '../lib/empleados.js'
import { logError } from '../lib/log.js'

const porNombre = (a, b) => a.nombre.localeCompare(b.nombre, 'es', { sensitivity: 'base' })

function DirectorioPersonas({ onVerPerfil }) {
  const [estado, setEstado] = useState({ cargando: true, personas: [], error: false })

  useEffect(() => {
    let vivo = true
    Promise.resolve().then(() => listEmpleados())
      .then(r => {
        const personas = (Array.isArray(r?.empleados) ? r.empleados : [])
          .filter(e => e && e.estado === 'activo' && (e.nombre ?? '').trim())
          .sort(porNombre)
        if (vivo) setEstado({ cargando: false, personas, error: false })
      })
      .catch(err => {
        logError('personas.directorio.error', { code: err?.code, status: err?.status })
        if (vivo) setEstado({ cargando: false, personas: [], error: true })
      })
    return () => { vivo = false }
  }, [])

  const { cargando, personas, error } = estado
  return (
    <section className="dk-personas" aria-label="Personas">
      <div className="dk-empleados__cab">
        <h2 className="dk-empleados__titulo">Personas</h2>
        {!cargando && !error && <Insignia>{personas.length}</Insignia>}
        <span className="dk-meta">tus compañeros de la empresa</span>
      </div>
      {cargando && <p className="dk-meta" role="status">Cargando…</p>}
      {error && <p className="dk-meta" role="alert">No se ha podido cargar el directorio de personas.</p>}
      {!cargando && !error && personas.length === 0 && <p className="dk-meta">Todavía no hay personas en la empresa.</p>}
      {personas.length > 0 && (
        <Tarjeta className="dk-empleados__fila">
          {personas.map((e, i) => (
            <button type="button" key={`${e.nombre}-${i}`} className="dk-persona" onClick={() => onVerPerfil?.(e)}>
              <Avatar iniciales={inicialesDe(e.nombre)} nombre={e.nombre} tamano={56} />
              <span className="dk-persona__txt">
                <span className="dk-persona__nombre">{e.nombre}</span>
                {(e.cargo ?? '').trim() && <small className="dk-persona__sub">{e.cargo.trim()}</small>}
                <small className="dk-persona__sub">{rolTexto(e.rol)}</small>
              </span>
            </button>
          ))}
        </Tarjeta>
      )}
    </section>
  )
}

export default function PersonasPanel({ onVerPerfil }) {
  const { capabilities, agents = [] } = useSession()
  const fotoDe = useFotoAgente()
  // GEN-PX-028: las cifras de la cabecera (solo las recibe el administrador) abren la gestión filtrada; se remonta con `key`.
  const [destino, setDestino] = useState({ clave: 0, filtro: null })
  const gestiona = !!capabilities?.menu?.some(m => m.id === 'admin' && m.visible && m.enabled)
  // Cabecera de Anne que emite el kernel en el ítem `personas`; aquí solo se pinta.
  const cab = capabilities?.menu?.find(m => m?.id === 'personas')?.cabecera
  const ag = cab?.agente && (agents.find(a => a.id === cab.agente.id) ?? cab.agente)
  const cifras = gestiona
    ? (cab?.cifras ?? []).map(c => ({
        texto: c.texto,
        onClick: () => setDestino(d => ({ clave: d.clave + 1, filtro: c.seccion === 'pendientes' ? { estado: 'pendiente' } : { id: c.seccion } })),
      }))
    : []
  return (
    <>
      {cab && (
        <CabeceraLanding
          agente={ag ? { nombre: cab.agente.nombre, rol: cab.agente.rol, foto: fotoDe(ag) } : null}
          titulo={cab.titulo}
          texto={cab.texto}
          cifras={cifras}
        />
      )}
      {gestiona ? <AdminPanel key={destino.clave} filtroInicial={destino.filtro} /> : <DirectorioPersonas onVerPerfil={onVerPerfil} />}
    </>
  )
}
